import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Building2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { createCompanyWithAdmin } from '../hooks/useMemberships'

export default function CreateCompanyModal({ onClose, onCreated }) {
    const { t } = useTranslation()
    const [name, setName] = useState('')
    const [location, setLocation] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose?.() }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [loading, onClose])

    const handleSubmit = async (event) => {
        event.preventDefault()
        if (!name.trim() || name.trim().length < 2) {
            setError(t('company.nameRequired'))
            return
        }
        setError('')
        setLoading(true)
        const res = await createCompanyWithAdmin(name.trim(), location.trim() || null)
        setLoading(false)
        if (!res.success) {
            setError(res.error || t('company.createError'))
            return
        }
        onCreated?.({ companyId: res.companyId, joinCode: res.joinCode })
    }

    return createPortal(
        <div className="ccm-overlay" onClick={() => !loading && onClose?.()}>
            <form className="ccm-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
                <header className="ccm-head">
                    <div className="ccm-head-icon">
                        <Building2 size={18} />
                    </div>
                    <div>
                        <h2>{t('company.createCompanyTitle')}</h2>
                        <p>{t('company.createCompanyDesc')}</p>
                    </div>
                    <button type="button" className="ccm-close" onClick={onClose} aria-label={t('common.close')}>
                        <X size={18} />
                    </button>
                </header>

                <div className="ccm-body">
                    <div className="form-group">
                        <label className="form-label">{t('company.companyName')}</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('company.companyNamePlaceholder')}
                            autoFocus
                            required
                            minLength={2}
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">{t('company.location')}</label>
                        <input
                            type="text"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            placeholder={t('company.locationPlaceholder')}
                            disabled={loading}
                        />
                    </div>

                    {error ? <div className="ccm-error">{error}</div> : null}

                    <div className="ccm-promo">
                        <strong>{t('company.proTip')}</strong>&nbsp;{t('company.proTipText')}
                    </div>
                </div>

                <footer className="ccm-foot">
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
                        {t('common.cancel')}
                    </button>
                    <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? t('company.creating') : t('company.createAndEnter')}
                    </button>
                </footer>
            </form>
        </div>,
        document.body,
    )
}
